<template>
  <div class="lv">
    <div class="lv-head">
      <div class="lv-icon">
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      </div>
      <div class="lv-head-text">
        <div class="lv-title-row">
          <h1 class="lv-title">Cast Statistics</h1>
          <span class="lv-locked-pill">
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            LOCKED
          </span>
        </div>
        <p class="lv-sub">
          Host-controlled access. Verify to view transcripts, recordings,
          insights & shorts.
        </p>
      </div>
    </div>

    <div class="lv-policy">
      <div class="lv-policy-label">HOST ACCESS POLICY</div>
      <div class="lv-policy-row">
        <span class="lv-policy-icon">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <polyline points="9 12 11 14 15 10" />
          </svg>
        </span>
        <div class="lv-policy-text">
          <div class="lv-policy-title">
            Private. Verifiable Credentials required
          </div>
          <div class="lv-policy-desc">
            Access restricted to holders of credentials approved by the host
            (e.g. accredited press, org membership).
          </div>
        </div>
      </div>
    </div>

    <div class="lv-section-label">HOW TO GET ACCESS</div>

    <form class="lv-email" @submit.prevent="submitEmail">
      <span class="lv-email-icon">
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
          <polyline points="22,6 12,13 2,6" />
        </svg>
      </span>
      <div class="lv-email-body">
        <div class="lv-email-title">Email me the deliverables</div>
        <div class="lv-email-desc">
          Request transcript &amp; recording by email once host approves.
        </div>
        <div class="lv-email-row">
          <input
            v-model="email"
            class="lv-email-input"
            type="email"
            placeholder="you@newsroom.com"
            required
          />
          <button class="lv-email-btn" type="submit" :disabled="requesting">
            {{ requesting ? 'Requesting…' : 'Request access' }}
          </button>
        </div>
      </div>
    </form>

    <button class="lv-verify" type="button" @click="$emit('verify')">
      <span class="lv-verify-icon">
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" opacity="0" />
          <path d="M12 11v6" />
          <path d="M12 8a3 3 0 0 0-3 3v3a3 3 0 0 0 6 0v-3a3 3 0 0 0-3-3z" />
          <path d="M9 14a3 3 0 0 1-3-3" />
          <path d="M15 14a3 3 0 0 0 3-3" />
          <path d="M12 21v-3" />
        </svg>
      </span>
      <span class="lv-verify-body">
        <span class="lv-verify-title">Verify with credentials</span>
        <span class="lv-verify-desc">
          Present your DID and required Verifiable Credentials. Selective
          disclosure. Your identity stays private.
        </span>
      </span>
      <span class="lv-verify-chev">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </span>
    </button>

    <p class="lv-privacy">
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
      Decast never sees your real identity. Verification happens
      cryptographically. Only the host's policy decides what unlocks.
    </p>

    <button
      v-if="loggedIn"
      type="button"
      class="lv-feedback-link"
      @click="$emit('open-feedback')"
    >
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
      Send Decast platform feedback for this cast
    </button>
  </div>
</template>

<script>
export default {
  name: 'LockedView',
  props: {
    loggedIn: { type: Boolean, default: false },
  },
  data() {
    return {
      email: '',
      requesting: false,
    };
  },
  methods: {
    submitEmail() {
      if (!this.email) return;
      this.requesting = true;
      this.$emit('request-access', this.email, () => {
        this.requesting = false;
      });
    },
  },
};
</script>

<style scoped>
.lv {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  color: #fff;
}
.lv-head {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 28px;
  flex-wrap: wrap;
}
.lv-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.85);
  flex-shrink: 0;
}
.lv-head-text {
  flex: 1;
  min-width: 0;
}
.lv-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.lv-title {
  font-size: 28px;
  font-weight: 700;
  color: #fff;
  margin: 0;
  line-height: 1.2;
  overflow-wrap: break-word;
  word-wrap: break-word;
}
.lv-locked-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.7);
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}
.lv-sub {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.55);
  margin: 6px 0 0;
  line-height: 1.55;
}

.lv-policy {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 18px 20px;
  margin-bottom: 28px;
}
.lv-policy-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  color: rgba(255, 255, 255, 0.45);
  text-transform: uppercase;
  margin-bottom: 12px;
}
.lv-policy-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.lv-policy-icon {
  color: #a78bfa;
  margin-top: 2px;
  flex-shrink: 0;
}
.lv-policy-text {
  flex: 1;
  min-width: 0;
}
.lv-policy-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 6px;
}
.lv-policy-desc {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.5;
}

.lv-section-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  margin-bottom: 12px;
}

.lv-email {
  display: flex;
  gap: 14px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 12px;
  padding: 18px 20px;
  margin-bottom: 14px;
}
.lv-email-icon {
  color: rgba(255, 255, 255, 0.7);
  margin-top: 2px;
  flex-shrink: 0;
}
.lv-email-body {
  flex: 1;
  min-width: 0;
}
.lv-email-title {
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 4px;
}
.lv-email-desc {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.55);
  margin-bottom: 12px;
  line-height: 1.5;
}
.lv-email-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}
.lv-email-input {
  min-width: 0;
}
@media (max-width: 540px) {
  .lv-email-input,
  .lv-email-btn {
    flex: 1 1 100%;
  }
  .lv-title {
    font-size: 24px;
  }
  .lv-icon {
    width: 48px;
    height: 48px;
  }
  .lv-email,
  .lv-policy,
  .lv-verify {
    padding: 14px 16px;
  }
  .lv-verify-desc,
  .lv-policy-desc {
    font-size: 12px;
  }
}
.lv-email-input {
  flex: 1;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 10px 14px;
  color: #fff;
  font-size: 14px;
  font-family: inherit;
  outline: none;
  transition: border-color 0.15s ease;
}
.lv-email-input:focus {
  border-color: rgba(211, 202, 87, 0.45);
}
.lv-email-input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}
.lv-email-btn {
  padding: 10px 18px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.03);
  color: #fff;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.lv-email-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.22);
}
.lv-email-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.lv-verify {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  background: rgba(211, 202, 87, 0.05);
  border: 1px solid rgba(211, 202, 87, 0.35);
  border-radius: 12px;
  padding: 18px 22px;
  text-align: left;
  cursor: pointer;
  color: #fff;
  font-family: inherit;
  transition: background 0.15s ease, border-color 0.15s ease,
    box-shadow 0.15s ease;
  box-shadow: 0 0 0 0 rgba(211, 202, 87, 0);
}
.lv-verify:hover {
  background: rgba(211, 202, 87, 0.09);
  border-color: rgba(211, 202, 87, 0.55);
  box-shadow: 0 0 24px rgba(211, 202, 87, 0.15);
}
.lv-verify-icon {
  color: #d3ca57;
  flex-shrink: 0;
}
.lv-verify-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.lv-verify-title {
  font-size: 15px;
  font-weight: 700;
  color: #fff;
}
.lv-verify-desc {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.55);
  line-height: 1.5;
}
.lv-verify-chev {
  color: rgba(255, 255, 255, 0.5);
  flex-shrink: 0;
}

.lv-privacy {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  margin: 16px 0 0;
  line-height: 1.5;
}
.lv-privacy svg {
  flex-shrink: 0;
}

.lv-feedback-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 18px;
  padding: 8px 14px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.lv-feedback-link:hover {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.25);
  color: #fff;
}
</style>
